import { useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Database,
  RadioTower,
  LineChart,
  Newspaper,
  Users,
  Building2,
  BarChart2,
  Zap,
  ShieldCheck,
  Code2,
  Tag,
  Languages,
  Coins,
  LayoutDashboard,
  Blocks,
  Braces,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { Marquee } from "@/components/Marquee";
import { Mockup } from "@/components/Mockup";
import { CtaStrip } from "@/components/CtaStrip";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -88 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const FEATURES = [
  {
    icon: Database,
    title: "Structured Election Data",
    body: "Every constituency, candidate, party and result — structured, standardised and searchable across elections.",
    testid: "feature-structured-data",
  },
  {
    icon: RadioTower,
    title: "Real-Time Intelligence",
    body: "Live seat tallies, majority tracking, constituency-level updates and interactive maps during active elections.",
    testid: "feature-realtime-intelligence",
  },
  {
    icon: LineChart,
    title: "Analytical Layer",
    body: "Swing analysis, vote-share change, margin patterns, historical comparisons and data-backed narrative leads.",
    testid: "feature-analytical-layer",
  },
];

const FLOW_STEPS = ["Data", "Visualisation", "Comparison", "Pattern", "Analysis", "Publishable Insight"];

const CAPABILITIES = [
  {
    title: "Seat Change Analysis",
    body: "Seats gained or lost against the previous election, by party and region.",
  },
  {
    title: "Vote Share Movement",
    body: "Percentage-point shifts in vote share, clearly distinguished from percentage growth.",
  },
  {
    title: "Margin Analysis",
    body: "Largest victories, narrowest wins, and the most closely contested constituencies.",
  },
  {
    title: "Constituency Turnover",
    body: "Which seats changed political control and where.",
  },
  {
    title: "Geographic Patterns",
    body: "Regional and district-level performance mapping.",
  },
  {
    title: "Historical Trends",
    body: "Multi-election trajectories for parties, candidates and constituencies.",
  },
];

const AUDIENCES = [
  {
    icon: Newspaper,
    title: "Journalists & News Desks",
    body: "Fast, structured access to election facts, constituency data, margins, vote shares, party performance and visual material — everything a reporter needs on election results day and after.",
    testid: "audience-journalists",
  },
  {
    icon: Users,
    title: "Political Analysts & Researchers",
    body: "Constituency-level historical analysis, geographic patterns, party and candidate comparisons, and structured evidence for political research.",
    testid: "audience-analysts",
  },
  {
    icon: Building2,
    title: "Media Organisations",
    body: "White-label election dashboards, embeddable widgets, branded result experiences and live-result modules your audience expects during elections.",
    testid: "audience-media-orgs",
  },
  {
    icon: BarChart2,
    title: "Data Journalism Platforms",
    body: "A structured data layer and analytical toolkit for building evidence-based election narratives.",
    testid: "audience-data-platforms",
  },
];

const VALUES = [
  { icon: Zap, title: "Speed", body: "Real-time constituency-level updates with minimal delay" },
  { icon: ShieldCheck, title: "Accuracy", body: "Structured, validated data with clear source attribution" },
  { icon: Code2, title: "Embeddable", body: "Plug-and-play widgets via iframe or script, compatible with any CMS" },
  { icon: Tag, title: "White Label", body: "Your brand, your domain, zero SevenM branding" },
  { icon: Languages, title: "Multilingual", body: "Content in English, Hindi and regional languages" },
  { icon: Coins, title: "Monetisable", body: "Built-in ad placement for Google AdSense and direct networks" },
];

const STATS = [
  { value: "543", label: "Lok Sabha Constituencies" },
  { value: "4,000+", label: "Assembly Constituencies" },
  { value: "Live", label: "Real-Time Updates" },
  { value: "1952+", label: "Historical Data Archive" },
];

const Home = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const mapX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const mapY = useTransform(sy, [-0.5, 0.5], [-10, 10]);

  const onHeroMouse = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      mx.set((e.clientX - rect.left) / rect.width - 0.5);
      my.set((e.clientY - rect.top) / rect.height - 0.5);
    },
    [mx, my]
  );

  return (
    <div data-testid="home-page">
      <Seo
        title="Anlytx for Elections — India's Election Intelligence Platform | Real-Time Dashboards, Constituency Data & Political Analysis"
        description="Anlytx for Elections is a real-time election intelligence platform that transforms raw election data into structured, searchable, comparable and analytically useful information — for journalists, political analysts and media organisations."
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden bg-[#0b1322]"
        onMouseMove={onHeroMouse}
        data-testid="home-hero"
      >
        <div className="absolute inset-0 grid-faint-dark opacity-70" />
        <div className="absolute -top-40 left-1/4 h-96 w-[60%] rounded-full bg-[#b91c1c]/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-16 sm:pt-24 lg:grid-cols-[1.15fr_0.85fr] lg:pb-28">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 px-4 py-1.5 font-mono-data text-[10px] font-medium uppercase tracking-[0.24em] text-slate-300"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#b91c1c]" />
              Election Intelligence Platform &bull; India
            </motion.p>
            <h1 className="mt-7 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.25}>India&apos;s Elections,</MaskedLine>
              <MaskedLine delay={0.4}>
                Explained Through <span className="text-[#f87171]">Data.</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
            >
              Anlytx for Elections is a real-time election intelligence platform that transforms raw election data into
              structured, searchable, comparable and analytically useful information — for journalists, political
              analysts and media organisations.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                type="button"
                onClick={() => scrollToId("platform")}
                data-testid="hero-explore-platform-btn"
                className="group inline-flex items-center gap-2 rounded-md bg-[#b91c1c] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#991b1b]"
              >
                Explore the Platform
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
              <Link
                to="/for-media"
                data-testid="hero-for-media-btn"
                className="group inline-flex items-center gap-2 rounded-md border border-slate-600 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-slate-400 hover:text-white"
              >
                For Media Partners
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-x-8 gap-y-2 font-mono-data text-[11px] uppercase tracking-[0.18em] text-slate-400"
              data-testid="hero-stat-strip"
            >
              <span>543 LS Seats</span>
              <span>4,000+ Assembly Seats</span>
              <span>28 States &bull; 8 UTs</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative hidden justify-center lg:flex"
            style={{ x: mapX, y: mapY }}
            data-testid="hero-india-visual"
          >
            <img
              src="/india-map.png"
              alt="Outline map of India"
              data-testid="hero-india-map"
              className="h-[460px] w-auto opacity-80"
              style={{ filter: "drop-shadow(0 0 44px rgba(185,28,28,0.22))" }}
            />
            <div className="absolute -left-4 top-16 rounded-md border border-white/10 bg-white/[0.06] px-3 py-2 font-mono-data text-[10px] uppercase tracking-[0.18em] text-slate-300 backdrop-blur-sm">
              Constituency Grid &bull; Live
            </div>
            <div className="absolute -right-2 bottom-20 rounded-md border border-white/10 bg-white/[0.06] px-3 py-2 font-mono-data text-[10px] uppercase tracking-[0.18em] text-slate-300 backdrop-blur-sm">
              Neutral by Design
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee
        items={[
          "543 Lok Sabha Constituencies",
          "4,000+ Assembly Constituencies",
          "28 States & 8 Union Territories",
          "Historical Data Archive",
          "Real-Time Results Pipeline",
          "Factual. Neutral. Methodology-First.",
        ]}
      />

      {/* What It Does */}
      <section className="bg-white" data-testid="home-features">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHead
            eyebrow="What It Does"
            title="Three Layers of Election Intelligence"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.testid} delay={i * 0.12}>
                <div
                  data-testid={f.testid}
                  className="group h-full rounded-xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#b91c1c]/40 hover:shadow-[0_20px_50px_-20px_rgba(185,28,28,0.22)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#1a2332]">
                    <f.icon className="h-5 w-5 text-white" />
                  </span>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-slate-900">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section id="platform" className="relative overflow-hidden bg-[#f8fafc]" data-testid="home-platform">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHead
            eyebrow="The Platform"
            title="The Live Results Dashboard"
            sub="Live election dashboard — constituency map, seat tally, party standings and candidate-level detail, all updating in real time."
          />
          <Reveal delay={0.15} className="mt-14">
            <Mockup
              label="Live Results Dashboard"
              src="/screenshots/dashboard-live.jpg"
              caption="Live election dashboard — breaking-news ticker, live insights, majority tracker, seat tally and party standings, updating in real time."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div
                  data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-lg border border-slate-200 bg-white px-6 py-5 text-center"
                >
                  <p className="font-mono-data text-2xl font-semibold tracking-tight text-[#1a2332]">{s.value}</p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Modes */}
      <section className="relative overflow-hidden bg-[#0b1322]" data-testid="delivery-modes-section">
        <div className="absolute inset-0 grid-faint-dark opacity-60" />
        <div className="absolute -top-32 left-0 h-80 w-[50%] rounded-full bg-[#b91c1c]/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHead
            dark
            eyebrow="Delivery Modes"
            title="One Engine. Three Ways to Deploy."
            sub="The same election data and analytics platform — delivered the way your team actually works."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: LayoutDashboard,
                num: "01",
                title: "Full White-Label Dashboard",
                body: "Your brand, your domain. A complete election-results experience — dashboards, maps, tallies and analysis — deployed entirely under your identity. Nothing to build.",
                cta: "For media organisations",
                to: "/for-media",
                testid: "mode-white-label",
              },
              {
                icon: Blocks,
                num: "02",
                title: "Easy-to-Integrate Widgets",
                body: "Drop live result modules — seat tallies, maps, party standings, candidate cards — into your own website or app with a simple iframe or script embed.",
                cta: "See widget options",
                to: "/for-media",
                testid: "mode-widgets",
              },
              {
                icon: Braces,
                num: "03",
                title: "API Mode",
                body: "For customers looking only for data. Structured constituency, results and historical data over a documented API — build your own experience on your own stack.",
                cta: "Explore API access",
                scroll: "api-access",
                testid: "mode-api",
              },
            ].map((m, i) => (
              <Reveal key={m.testid} delay={i * 0.12}>
                <div
                  data-testid={m.testid}
                  className="flex h-full flex-col rounded-xl border border-white/10 bg-white/[0.04] p-8 transition-colors duration-300 hover:border-[#b91c1c]/60"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#b91c1c]">
                      <m.icon className="h-5 w-5 text-white" />
                    </span>
                    <span className="font-mono-data text-xs font-semibold tracking-[0.2em] text-slate-500">
                      {m.num}
                    </span>
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-white">{m.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">{m.body}</p>
                  {m.scroll ? (
                    <button
                      type="button"
                      onClick={() => scrollToId(m.scroll)}
                      data-testid={`${m.testid}-cta`}
                      className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-red-400 transition-colors hover:text-red-300"
                    >
                      {m.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <Link
                      to={m.to}
                      data-testid={`${m.testid}-cta`}
                      className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-red-400 transition-colors hover:text-red-300"
                    >
                      {m.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Analytical Difference */}
      <section className="bg-white" data-testid="home-analytics">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHead
            eyebrow="The Analytical Difference"
            title="From Raw Results to Publishable Insight"
            sub="Anlytx for Elections doesn't just show who won. It shows what changed, where it changed, and what patterns emerge from the numbers."
          />

          <Reveal delay={0.1} className="mt-14">
            <div
              className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:gap-0"
              data-testid="insight-flow-diagram"
            >
              {FLOW_STEPS.map((step, i) => (
                <div key={step} className="flex flex-1 flex-col items-center gap-3 md:flex-row md:gap-0">
                  <div
                    className="flex w-full flex-col items-center rounded-lg border border-slate-200 bg-[#f8fafc] px-4 py-4 text-center text-slate-800 transition-colors duration-300 hover:border-[#b91c1c]/50 md:min-h-[92px] md:justify-center"
                    data-testid={`flow-step-${i + 1}`}
                  >
                    <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="mt-1 font-heading text-sm font-semibold leading-tight">{step}</span>
                  </div>
                  {i < FLOW_STEPS.length - 1 && (
                    <>
                      <ArrowRight className="hidden h-4 w-4 shrink-0 text-[#b91c1c]/60 md:mx-2 md:block" />
                      <ArrowDown className="h-4 w-4 shrink-0 text-[#b91c1c]/60 md:hidden" />
                    </>
                  )}
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.1}>
                <div
                  data-testid={`capability-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-colors duration-300 hover:border-[#b91c1c]/40"
                >
                  <span className="font-mono-data text-xs font-semibold tracking-[0.2em] text-[#b91c1c]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="relative overflow-hidden bg-[#f8fafc]" data-testid="home-audiences">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHead eyebrow="Who It's For" title="Built for the People Who Cover Elections" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.testid} delay={(i % 2) * 0.12}>
                <div
                  data-testid={a.testid}
                  className="flex h-full gap-5 rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(26,35,50,0.3)]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#fef2f2]">
                    <a.icon className="h-5 w-5 text-[#b91c1c]" />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{a.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Anlytx for Elections */}
      <section className="bg-white" data-testid="home-why">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHead eyebrow="Why Anlytx for Elections" title="Why Newsrooms Choose Anlytx for Elections" />
          <div className="mt-14 grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <div key={v.title} className="bg-white p-7" data-testid={`value-${v.title.toLowerCase()}`}>
                <Reveal delay={(i % 3) * 0.08}>
                  <div className="flex items-center gap-3">
                    <v.icon className="h-5 w-5 text-[#b91c1c]" />
                    <h3 className="font-heading text-base font-semibold text-slate-900">{v.title}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.body}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Access Highlight */}
      <section id="api-access" className="relative overflow-hidden bg-[#0b1322]" data-testid="api-access-section">
        <div className="absolute inset-0 grid-faint-dark opacity-70" />
        <div className="absolute -bottom-32 right-0 h-80 w-[50%] rounded-full bg-[#b91c1c]/20 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 sm:py-24 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">
              Delivery Mode 03 &bull; API Access
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Your Dashboard. Our Data.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
              Pull structured election data straight into your own products — constituency results, seat tallies,
              party standings and historical records over a documented API. Build exactly the experience your
              audience needs, on your own stack.
            </p>
            <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.04] p-5">
              <p className="text-sm leading-relaxed text-slate-300">
                Prefer plug-and-play? Our ready-to-use widgets and white-label dashboards deliver the same data with
                zero integration work.{" "}
                <Link
                  to="/for-media"
                  data-testid="api-widgets-link"
                  className="font-semibold text-red-400 transition-colors hover:text-red-300"
                >
                  See the media offering
                </Link>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div
              className="overflow-hidden rounded-xl border border-slate-700/60 bg-[#0a0f1c] shadow-[0_28px_70px_-28px_rgba(0,0,0,0.7)]"
              data-testid="api-code-block"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
                <span className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-slate-400">
                  GET /v1/results/ls-2024?state=up
                </span>
                <span className="flex gap-1.5" aria-hidden="true">
                  <span className="h-2 w-2 rounded-full bg-[#f97316]/70" />
                  <span className="h-2 w-2 rounded-full bg-[#16a34a]/70" />
                  <span className="h-2 w-2 rounded-full bg-[#b91c1c]/70" />
                </span>
              </div>
              <pre className="overflow-x-auto p-5 font-mono-data text-xs leading-relaxed text-slate-300">
{`{
  "constituency": "Example Nagar",
  "status": "declared",
  "margin_votes": 48210,
  "winner_vote_share": 51.6,
  "provisional": false,
  "updated_at": "2024-06-04T14:32:11+05:30"
}`}
              </pre>
              <div className="border-t border-white/10 px-5 py-3 font-mono-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Illustrative payload &bull; structured &amp; documented
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaStrip
        testid="home-cta-strip"
        heading="Be Election-Ready Before the Results Go Live"
        subheading="Whether you need a white-label dashboard for your newsroom or structured election data for your research — let's talk."
        primary={{ label: "Request a Demo", to: "/contact?type=demo" }}
        secondary={{ label: "Discuss a Data Requirement", to: "/contact?type=data" }}
      />

      {/* Methodology Teaser */}
      <section className="bg-white" data-testid="home-methodology-teaser">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-[1fr_0.6fr]">
            <Reveal>
              <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-[#b91c1c]">
                Methodology
              </p>
              <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                How We Handle the Data
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                Anlytx for Elections explicitly separates data, calculation and interpretation. Every metric has a
                transparent definition. Provisional data is labelled. Historical comparisons account for delimitation
                and boundary changes. Our methodology is documented and open to scrutiny.
              </p>
              <Link
                to="/methodology"
                data-testid="methodology-teaser-link"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#b91c1c] transition-colors hover:text-[#991b1b]"
              >
                Read Our Methodology
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { n: "01", t: "Data", d: "Reported figures" },
                  { n: "02", t: "Calculation", d: "Derived metrics" },
                  { n: "03", t: "Interpretation", d: "Analytical conclusions" },
                ].map((l) => (
                  <div
                    key={l.n}
                    className="rounded-lg border-l-[3px] border-[#b91c1c] bg-[#f8fafc] p-4"
                    data-testid={`layer-${l.n}`}
                  >
                    <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-slate-400">{l.n}</p>
                    <p className="mt-1 font-heading text-sm font-semibold text-slate-900">{l.t}</p>
                    <p className="mt-1 text-xs text-slate-500">{l.d}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
